/* eslint-disable no-unused-vars */
import React, { useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import d3 from '../../utils/d3Importer';
import { MapStyles } from './mapstyles';
import geoData from '../../../static/data/fishingZones_v1_merged.json';
import {
  clearInteractiveAreaCode,
  setHoveredAreaCode,
  setPinnedAreaCode,
} from '../../state/slices/data';

const WIDTH = 960;
const HEIGHT = 500;
const LEGEND_WIDTH = 160;
const LEGEND_HEIGHT = 10;
const LEGEND_STEPS = 10;

export default function Map() {
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const tooltipRef = useRef(null);
  const dispatch = useDispatch();
  const hoveredAreaCode = useSelector((state) => state.data.hoveredAreaCode);
  const pinnedAreaCode = useSelector((state) => state.data.pinnedAreaCode);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    const tooltip = d3.select(tooltipRef.current);
    svg.selectAll('*').remove();
    tooltip.style('display', 'none');

    const reverseRing = (ring) => [...ring].reverse();
    const reverseGeometry = (geometry) => {
      if (geometry.type === 'Polygon') {
        return {
          ...geometry,
          coordinates: geometry.coordinates.map(reverseRing),
        };
      }

      if (geometry.type === 'MultiPolygon') {
        return {
          ...geometry,
          coordinates: geometry.coordinates.map((polygon) => {
            return polygon.map(reverseRing);
          }),
        };
      }

      return geometry;
    };

    // Some source polygons are oriented as the spherical complement (inside-out).
    // Flip those features so D3 draws the intended zone, not almost the whole globe.
    const normalizedGeoData = {
      ...geoData,
      features: geoData.features.map((feature) => {
        const isInsideOut = d3.geoArea(feature) > 2 * Math.PI;
        if (!isInsideOut) return feature;

        return {
          ...feature,
          geometry: reverseGeometry(feature.geometry),
        };
      }),
    };

    // Projection + path
    const projection = d3
      .geoNaturalEarth1()
      .fitSize([WIDTH, HEIGHT], normalizedGeoData);

    const pathGen = d3.geoPath().projection(projection);

    // Paint larger polygons first so smaller regions are not hidden underneath.
    const sortedFeatures = [...normalizedGeoData.features].sort(
      (a, b) => d3.geoArea(b) - d3.geoArea(a),
    );

    // Reference-inspired and color-blind-safe: orange -> aqua -> deep blue.
    const colorScale = d3
      .scaleLinear()
      .domain([0, 50, 100])
      .range(['#d97706', '#2aa7c9', '#2b2d8f']);
    const sphere = { type: 'Sphere' };
    const defs = svg.append('defs');
    const mapClipId = 'map-projection-clip';

    defs
      .append('clipPath')
      .attr('id', mapClipId)
      .append('path')
      .datum(sphere)
      .attr('d', pathGen);
    const clampPct = (value) => Math.max(0, Math.min(100, value));
    const parsePct = (value) => {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const formatPct = (value) => `${parsePct(value).toFixed(1)}%`;
    const getAreaName = (feature) =>
      feature.properties.AREA || feature.properties.F_NAME || 'Unknown area';
    const isArcticSea = (feature) =>
      feature.properties.F_CODE === '18' ||
      feature.properties.F_NAME === 'Arctic Sea';

    const pinnedAreaCodes = Array.isArray(pinnedAreaCode)
      ? pinnedAreaCode
      : pinnedAreaCode
        ? [pinnedAreaCode]
        : [];
    const hoveredAreaCodes = Array.isArray(hoveredAreaCode)
      ? hoveredAreaCode
      : hoveredAreaCode
        ? [hoveredAreaCode]
        : [];
    const activeAreaCodes = pinnedAreaCodes.length
      ? pinnedAreaCodes
      : hoveredAreaCodes;

    const positionTooltip = (event) => {
      const wrap = wrapRef.current;
      const tip = tooltipRef.current;
      if (!wrap || !tip) return;

      const wrapRect = wrap.getBoundingClientRect();
      const offset = 14;
      let left = event.clientX - wrapRect.left + offset;
      let top = event.clientY - wrapRect.top + offset;

      const tipWidth = tip.offsetWidth || 220;
      const tipHeight = tip.offsetHeight || 120;
      const maxLeft = wrapRect.width - tipWidth - 8;
      const maxTop = wrapRect.height - tipHeight - 8;

      left = Math.max(8, Math.min(left, maxLeft));
      top = Math.max(8, Math.min(top, maxTop));

      tooltip.style('left', `${left}px`).style('top', `${top}px`);
    };

    const renderTooltip = (event, feature) => {
      const sustainableRaw = parsePct(feature.properties.SUSTAINABLE);
      const unsustainableRaw = parsePct(feature.properties.UNSUSTAINABLE);
      const total = Math.max(sustainableRaw + unsustainableRaw, 0.0001);
      const sustainableBar = clampPct((sustainableRaw / total) * 100);
      const unsustainableBar = clampPct((unsustainableRaw / total) * 100);

      tooltip.style('display', 'block').html(`
          <div class="map-tooltip-title">${getAreaName(feature)}</div>
          <div class="map-tooltip-bar-wrap">
            <div class="map-tooltip-bar map-tooltip-bar-unsustainable" style="width:${unsustainableBar}%"></div>
            <div class="map-tooltip-bar map-tooltip-bar-sustainable" style="width:${sustainableBar}%"></div>
          </div>
          <div class="map-tooltip-row">
            <span class="map-tooltip-dot map-tooltip-dot-unsustainable"></span>
            <span>Unsustainable</span>
            <strong>${formatPct(unsustainableRaw)}</strong>
          </div>
          <div class="map-tooltip-row">
            <span class="map-tooltip-dot map-tooltip-dot-sustainable"></span>
            <span>Sustainable</span>
            <strong>${formatPct(sustainableRaw)}</strong>
          </div>
        `);

      positionTooltip(event);
    };

    const hideTooltip = () => {
      tooltip.style('display', 'none');
    };

    // ── Ocean background ──────────────────────────────────────────
    svg
      .append('path')
      .datum(sphere)
      .attr('class', 'ocean-sphere')
      .attr('d', pathGen)
      .attr('fill', 'rgb(0, 71, 76)');

    // ── Fishing zone polygons ──────────────────────────────────────
    const zonesGroup = svg
      .append('g')
      .attr('class', 'zones')
      .attr('clip-path', `url(#${mapClipId})`);

    const clearActive = () => {
      zonesGroup.selectAll('path.zone-path').classed('is-active', false);
    };

    const syncActiveState = () => {
      clearActive();

      if (!activeAreaCodes.length) {
        hideTooltip();
        return;
      }

      const activePath = zonesGroup
        .selectAll('path.zone-path')
        .filter((feature) =>
          activeAreaCodes.includes(feature.properties.F_CODE),
        );

      if (activePath.empty()) {
        hideTooltip();
        return;
      }

      activePath.classed('is-active', true);
    };

    const paths = zonesGroup
      .selectAll('path')
      .data(sortedFeatures, (f) => f.properties.F_CODE)
      .join('path')
      .attr('class', 'zone-path')
      .attr('d', (f) => pathGen(f))
      .attr('fill-rule', 'evenodd')
      .attr('fill', (f) => {
        const val = parseFloat(f.properties.SUSTAINABLE);
        return Number.isNaN(val) ? '#444' : colorScale(val);
      })
      .on('mouseenter', (event, feature) => {
        if (isArcticSea(feature)) {
          d3.select(event.currentTarget).classed('is-active', false);
          hideTooltip();
          return;
        }

        if (
          pinnedAreaCodes.length &&
          !pinnedAreaCodes.includes(feature.properties.F_CODE)
        )
          return;
        dispatch(setHoveredAreaCode(feature.properties.F_CODE));
        renderTooltip(event, feature);
      })
      .on('mousemove', (event, feature) => {
        if (isArcticSea(feature)) {
          hideTooltip();
          return;
        }

        if (
          pinnedAreaCodes.length &&
          !pinnedAreaCodes.includes(feature.properties.F_CODE)
        )
          return;
        renderTooltip(event, feature);
      })
      .on('mouseleave', () => {
        if (pinnedAreaCodes.length) return;
        dispatch(setHoveredAreaCode(null));
        hideTooltip();
      })
      .on('click', (event, feature) => {
        if (isArcticSea(feature)) {
          event.stopPropagation();
          d3.select(event.currentTarget).classed('is-active', false);
          hideTooltip();
          return;
        }

        event.stopPropagation();
        const code = feature.properties.F_CODE;

        if (pinnedAreaCodes.length === 1 && pinnedAreaCodes[0] === code) {
          dispatch(clearInteractiveAreaCode());
          hideTooltip();
          return;
        }

        dispatch(setPinnedAreaCode(code));
        dispatch(setHoveredAreaCode(null));
        renderTooltip(event, feature);
      });

    svg.on('click', () => {
      dispatch(clearInteractiveAreaCode());
      hideTooltip();
    });

    syncActiveState();

    // ── Legend ────────────────────────────────────────────────────
    const legendX = (WIDTH - LEGEND_WIDTH) / 2;
    const legendY = HEIGHT - 40;
    const legendGroup = svg
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${legendX}, ${legendY})`);

    // Build gradient
    const gradId = 'sustainable-gradient';
    const grad = defs
      .append('linearGradient')
      .attr('id', gradId)
      .attr('x1', '0%')
      .attr('x2', '100%');

    for (let i = 0; i <= LEGEND_STEPS; i += 1) {
      const t = i / LEGEND_STEPS;
      grad
        .append('stop')
        .attr('offset', `${t * 100}%`)
        .attr('stop-color', colorScale(t * 100));
    }

    legendGroup
      .append('rect')
      .attr('width', LEGEND_WIDTH)
      .attr('height', LEGEND_HEIGHT)
      .attr('rx', 2)
      .attr('fill', `url(#${gradId})`);

    legendGroup
      .append('text')
      .attr('class', 'legend-title')
      .attr('x', LEGEND_WIDTH / 2)
      .attr('y', -6)
      .attr('text-anchor', 'middle')
      .text('Sustainable Stocks (%)');

    legendGroup
      .append('text')
      .attr('class', 'legend-label')
      .attr('x', 0)
      .attr('y', LEGEND_HEIGHT + 12)
      .text('0%');

    legendGroup
      .append('text')
      .attr('class', 'legend-label')
      .attr('x', LEGEND_WIDTH / 2)
      .attr('y', LEGEND_HEIGHT + 12)
      .attr('text-anchor', 'middle')
      .text('50%');

    legendGroup
      .append('text')
      .attr('class', 'legend-label')
      .attr('x', LEGEND_WIDTH)
      .attr('y', LEGEND_HEIGHT + 12)
      .attr('text-anchor', 'end')
      .text('100%');
  }, [dispatch, hoveredAreaCode, pinnedAreaCode]);

  return (
    <MapStyles ref={wrapRef}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
      />
      <div ref={tooltipRef} className="map-tooltip" />
    </MapStyles>
  );
}
